import { createClient } from '@supabase/supabase-js'
import { getOrganizationIdFromURL } from '../utils/tokenHandler.js'

/**
 * Supabase Client Configuration
 * Multi-tenant setup with organization ID validation
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Environment variables check:', {
    VITE_SUPABASE_URL: supabaseUrl ? 'present' : 'missing',
    VITE_SUPABASE_ANON_KEY: supabaseAnonKey ? 'present' : 'missing',
    allEnvKeys: Object.keys(import.meta.env).filter(key => key.startsWith('VITE_'))
  })
  throw new Error('Missing Supabase environment variables')
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey)

/**
 * Gets the current organization ID from URL
 * This should be validated on the backend for security
 * @returns {string|null} Organization ID
 */
export function getCurrentOrganizationId() {
  return getOrganizationIdFromURL()
}

/**
 * Validates organization ID before database operations
 * This is a client-side check - backend validation is required!
 * @param {string} orgId - Organization ID to validate
 * @returns {boolean} True if valid format
 */
export function validateOrganizationId(orgId) {
  if (!orgId || typeof orgId !== 'string') {
    return false
  }
  // Add your organization ID validation logic here
  return orgId.length > 0
}

/**
 * Fetches data from Supabase with organization ID filter
 * @param {string} table - Table name
 * @param {string} orgId - Organization ID (optional, will use current if not provided)
 * @param {Object} options - Query options
 * @returns {Promise} Query result
 */
export async function fetchOrgData(table, orgId = null, options = {}) {
  const currentOrgId = orgId || getCurrentOrganizationId()
  
  if (!validateOrganizationId(currentOrgId)) {
    throw new Error('Invalid or missing organization ID')
  }
  
  let query = supabase
    .from(table)
    .select(options.select || '*')
    .eq('organization_id', currentOrgId)
  
  if (options.filter) {
    Object.entries(options.filter).forEach(([key, value]) => {
      query = query.eq(key, value)
    })
  }
  
  if (options.orderBy) {
    query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending !== false })
  }
  
  const { data, error } = await query
  
  if (error) {
    console.error('Supabase Error:', error)
    throw error
  }
  
  return data
}

/**
 * Inserts data into Supabase with organization ID
 * @param {string} table - Table name
 * @param {Object} data - Data to insert
 * @param {string} orgId - Organization ID (optional, will use current if not provided)
 * @returns {Promise} Insert result
 */
export async function insertOrgData(table, data, orgId = null) {
  const currentOrgId = orgId || getCurrentOrganizationId()
  
  if (!validateOrganizationId(currentOrgId)) {
    throw new Error('Invalid or missing organization ID')
  }
  
  const dataWithOrg = {
    ...data,
    organization_id: currentOrgId
  }
  
  const { data: result, error } = await supabase
    .from(table)
    .insert(dataWithOrg)
    .select()
  
  if (error) {
    console.error('Supabase Error:', error)
    throw error
  }
  
  return result
}

/**
 * Updates data in Supabase with organization ID filter
 * @param {string} table - Table name
 * @param {Object} data - Data to update
 * @param {string} orgId - Organization ID (optional, will use current if not provided)
 * @returns {Promise} Update result
 */
export async function updateOrgData(table, data, orgId = null, filter = {}) {
  const currentOrgId = orgId || getCurrentOrganizationId()
  
  if (!validateOrganizationId(currentOrgId)) {
    throw new Error('Invalid or missing organization ID')
  }
  
  let query = supabase
    .from(table)
    .update(data)
    .eq('organization_id', currentOrgId)
  
  Object.entries(filter).forEach(([key, value]) => {
    query = query.eq(key, value)
  })
  
  const { data: result, error } = await query.select()
  
  if (error) {
    console.error('Supabase Error:', error)
    throw error
  }
  
  return result
}
